import dayjs from "dayjs";
import { t } from "i18next";
import _ from "lodash";

import {
  INDEX_NOT_FOUND,
  MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION,
} from "../../constants";
import {
  SimpleResult,
  type IPassengerInfo,
  type IRideDto,
  type IRideMetadata,
  type IStationInfo,
  type TStationComposedId,
} from "../../dto";
import { DirectionEnum, type TimeZoneEnum } from "../../enums";
import { simpleResultTrue } from "../../models";
import {
  appendRemovedAreaWaypoint,
  toRemovedAreaWaypoint,
} from "../area-waypoints.helpers";
import { unique } from "../array.helpers";
import {
  addMinutes,
  addSeconds,
  calculateAbsDurationInSeconds,
  dateTimeWithoutSeconds,
  dateToComparableNumber,
  formatTimeISO,
  isValidDateTime,
  resolveAppliedLegSeconds,
  timeDiffMinutesSigned,
  toCumulativeSeconds,
} from "../date.helpers";
import { Log } from "../log.helpers";
import { hasKeys } from "../objects.helpers";
import { getRideBoardingSegmentDurationSeconds } from "../production/ride-boarding-segment-duration.helpers";
import {
  ensureRoutingWaypointsAreInterior,
  filterRealRouteStations,
  getRealRouteEndpoints,
  isStationRouteWaypointStation,
} from "../station-route";
import { logShiftTimeCheckSkippedByAllowOverride } from "./ride-shift-time-override.helpers";
import { getStationAreasInfo } from "./ride-station-areas.helpers";
import {
  areStationsEqual,
  stationComposedIdMatch,
} from "./ride-station-composed-id.helpers";
import { extractDurations } from "./ride.station-route.helpers";
import { rideShouldCleanupEmptyStations } from "./rides.fixed.helpers";
import {
  calculatePassengersInfoSeatsCount,
  calculateRideSiteIds,
} from "./rides.helpers";

interface IRideNormalizeMetadataOptions {
  skipReorderStationsByTime?: boolean;
  cleanupEmptyStations?: boolean;
  isRideHasIndependentDayAdjustment?: boolean;
  direction: DirectionEnum;
  rideSiteId?: number | null;
}

export const sortStationsByTime = (
  stations: IStationInfo[],
): IStationInfo[] => {
  return [...stations].sort((a, b) => {
    if (!isValidDateTime(a.arrivalTime) || !isValidDateTime(b.arrivalTime)) {
      return 0;
    }

    const date1 = dateToComparableNumber(a.arrivalTime);
    const date2 = dateToComparableNumber(b.arrivalTime);

    return date1 - date2;
  });
};

export const sortPassengersByTime = (
  passengers: IPassengerInfo[],
  stations: IStationInfo[],
  direction: DirectionEnum,
): IPassengerInfo[] => {
  return [...passengers].sort((a, b) => {
    const dtA = calculateStationTime(
      direction === DirectionEnum.DropOff ? a.from : a.to,
      stations,
    );
    const dtB = calculateStationTime(
      direction === DirectionEnum.DropOff ? b.from : b.to,
      stations,
    );

    if (!dayjs(dtA).isValid() || !dayjs(dtB).isValid()) {
      return 0;
    }

    const date1 = dateToComparableNumber(dtA);
    const date2 = dateToComparableNumber(dtB);
    return date1 - date2;
  });
};

export const calculateStationTime = (
  stationId?: number,
  stationInfos?: IStationInfo[],
): TDateISO | undefined => {
  if (!stationId || !stationInfos?.length) {
    return undefined;
  }

  const station = stationInfos
    ? stationInfos.find(s => s.id === stationId || 0)
    : undefined;

  return station?.arrivalTime;
};

const checkAndFixPassengers = (
  passengers: IPassengerInfo[] | undefined,
): IPassengerInfo[] => {
  if (!passengers?.length) {
    return [];
  }

  return passengers
    .filter(pass => pass)
    .map(pass => {
      const seats = pass.seats && +pass.seats ? +pass.seats : 1;
      return { ...pass, seats };
    });
};

const recountStationsList = (
  stations: IStationInfo[] | undefined,
  passengers: IPassengerInfo[] | undefined,
): IStationInfo[] => {
  if (!stations?.length || !passengers?.length) {
    return stations || [];
  }

  return stations.map(s => {
    const isBoarding = passengers.some(p => p.from === s.id);
    const isDropoff = passengers.some(p => p.to === s.id);

    return { ...s, isBoarding, isDropoff };
  });
};

export const normalizeRideMetadata = (
  metadata: IRideMetadata | undefined,
  options: IRideNormalizeMetadataOptions,
): IRideMetadata => {
  if (!metadata?.stationsInfo?.length) {
    return metadata || {};
  }

  const {
    stationsInfo,
    passengersInfo,
    distances,
    durations,
    totalDuration,
    totalDistance,
  } = metadata;

  const { skipReorderStationsByTime, cleanupEmptyStations, direction } =
    options || {};

  const stationsRecounted = recountStationsList(stationsInfo, passengersInfo);
  const sortedStationsRaw = skipReorderStationsByTime
    ? stationsRecounted
    : sortStationsByTime(stationsRecounted);

  const passengersChecked = checkAndFixPassengers(passengersInfo || []);

  const sortedStations = cleanupEmptyStations
    ? sortedStationsRaw.filter(
        station =>
          isStationRouteWaypointStation(station) ||
          passengersChecked.some(
            passenger =>
              station.id === passenger.from || station.id === passenger.to,
          ),
      )
    : sortedStationsRaw;

  const passengersSorted = sortPassengersByTime(
    passengersChecked,
    sortedStations,
    direction || DirectionEnum.PickUp,
  );
  const {
    stationIds,
    pickupStationsIds,
    dropoffStationsIds,
    areaIds,
    innerAreaIds,
  } = getStationAreasInfo(sortedStations);
  const passengerIds = passengersSorted
    .filter(x => +x.id)
    .map(x => +x.id)
    .filter(Number);
  const passengersCount = passengerIds.length;
  const seatsCount = calculatePassengersInfoSeatsCount(passengersSorted);

  const unitIds = unique(
    passengersSorted
      ?.filter(x => x.unitId && Number(x.unitId))
      ?.map(x => Number(x.unitId)) || [],
  );

  const siteIds = calculateRideSiteIds(passengersSorted);

  const passengerTypeIds = unique(
    passengersSorted
      ?.filter(x => x.passengerTypeId && Number(x.passengerTypeId))
      ?.map(x => Number(x.passengerTypeId)) || [],
  );

  const durationsFromStations = extractDurations(sortedStations);
  const totalDistanceComputed = calculateTotalDistance({
    distances,
    totalDistanceFallback: totalDistance,
  });
  const totalDurationComputed = calculateTotalDurations({
    durationsFromStations: durationsFromStations,
    durationsFromMetadata: durations,
    totalDurationFallback: totalDuration,
  });

  const boardingSegmentDuration =
    getRideBoardingSegmentDurationSeconds({
      metadata: {
        stationsInfo: sortedStations,
        durations: durationsFromStations.length
          ? durationsFromStations
          : durations,
        passengersInfo: passengersSorted,
      },
      direction,
    }) || undefined;

  return {
    ...metadata,
    areaIds,
    innerAreaIds,
    stationsInfo: sortedStations,
    stationIds,
    pickupStationsIds,
    dropoffStationsIds,
    passengerIds,
    passengersCount,
    passengersInfo: passengersSorted,
    seatsCount,
    passengerTypeIds,
    unitIds,
    siteIds,
    totalDistance: totalDistanceComputed,
    totalDuration: totalDurationComputed,
    boardingSegmentDuration,
  };
};

export const reorderStationsByOrderedArray = ({
  metadata,
  stationNewOrderIds,
  direction,
}: {
  metadata: IRideMetadata | undefined;
  stationNewOrderIds: TStationComposedId[];
  direction: DirectionEnum;
}): IRideMetadata | undefined => {
  if (!metadata?.stationsInfo?.length || !stationNewOrderIds?.length) {
    return metadata;
  }

  const stationInfos = metadata.stationsInfo;

  const stationsInfoFiltered = stationNewOrderIds
    .map(composedId =>
      stationInfos.find(s =>
        stationComposedIdMatch({ station: s, composedId }),
      ),
    )
    .filter(x => x) as IStationInfo[];

  const stationIdsFiltered = stationsInfoFiltered.map(x => x.id || 0);
  const metadataUpdated: IRideMetadata = {
    ...metadata,
    stationIds: stationIdsFiltered,
    stationsInfo: stationsInfoFiltered,
  };
  const metadataTransformed = normalizeRideMetadata(metadataUpdated, {
    direction,
    skipReorderStationsByTime: true,
  });
  return metadataTransformed;
};

export const removePassengersFromRideDtoMetadata = (
  rideDto: Partial<IRideDto>,
  passengersToRemoveIds: number[],
): IRideMetadata => {
  const { metadata } = rideDto;

  if (!metadata || !hasKeys(metadata) || !passengersToRemoveIds?.length) {
    return {};
  }

  const passengersToRemoveIdsSet = new Set(passengersToRemoveIds);
  const metadataUpdated: IRideMetadata = {
    ...metadata,
    passengerIds:
      metadata.passengerIds?.filter(x => !passengersToRemoveIdsSet.has(x)) ||
      [],
    passengersInfo:
      metadata.passengersInfo?.filter(
        x => !passengersToRemoveIdsSet.has(x.id),
      ) || [],
  };

  const cleanupEmptyStations = rideShouldCleanupEmptyStations(rideDto);
  const metadataTransformed = normalizeRideMetadata(metadataUpdated, {
    direction: rideDto.direction || DirectionEnum.PickUp,
    cleanupEmptyStations,
  });

  return metadataTransformed;
};

export const removeEmptyStationFromRideDtoMetadata = ({
  rideDto,
  station,
  isRideWithEmptyStationsAllowed,
}: {
  rideDto: Partial<IRideDto>;
  station: IStationInfo;
  isRideWithEmptyStationsAllowed?: boolean;
}): IRideMetadata => {
  const { metadata } = rideDto;

  if (!metadata || !hasKeys(metadata) || !station?.id) {
    return metadata || {};
  }

  const stationsAfterRemove =
    metadata.stationsInfo?.filter(
      x => !areStationsEqual({ a: x, b: station }),
    ) || [];
  const stationsInfo = ensureRoutingWaypointsAreInterior(stationsAfterRemove);
  const stationIds = filterRealRouteStations(stationsInfo).map(x => x.id);
  const removedAreaWaypoint = toRemovedAreaWaypoint(station);

  const metadataUpdated: IRideMetadata = {
    ...metadata,
    stationIds,
    stationsInfo,
    // The backend syncs area defaults on every rebuild; this record keeps the deleted W from coming back.
    ...(removedAreaWaypoint
      ? {
          removedAreaWaypoints: appendRemovedAreaWaypoint({
            removedAreaWaypoints: metadata.removedAreaWaypoints,
            removedAreaWaypoint,
          }),
        }
      : {}),
  };

  const cleanupEmptyStations = rideShouldCleanupEmptyStations(
    rideDto,
    isRideWithEmptyStationsAllowed,
  );

  const metadataTransformed = normalizeRideMetadata(metadataUpdated, {
    direction: rideDto.direction || DirectionEnum.PickUp,
    cleanupEmptyStations,
  });
  return metadataTransformed;
};

export const isUpdatedStationTimeInsideShiftLimits = ({
  rideDto,
  timeOffsetMinutes,
  timeFormat,
  timezone,
  allowShiftTimeOverrideDuringManualAction = false,
}: {
  rideDto: Partial<IRideDto>;
  timeOffsetMinutes: number;
  timeFormat: string;
  timezone: TimeZoneEnum;
  allowShiftTimeOverrideDuringManualAction?: boolean;
}): boolean => {
  const stations = rideDto?.metadata?.stationsInfo ?? [];
  const passengers = rideDto?.metadata?.passengersInfo || [];

  if (!passengers.filter(x => x.shiftHour)?.length) {
    Log.debug("No passengers have shift time limitation", { ...passengers });
    return true;
  }

  const direction = rideDto.direction;

  const { first: firstRealStation, last: lastRealStation } =
    getRealRouteEndpoints(stations);

  const passShiftTimes = passengers
    .filter(x => x.shiftHour !== undefined)
    .map(x => dayjs.utc(x.shiftHour).valueOf());
  const minShiftTimeMs = Math.min(...passShiftTimes);
  const minShiftTime =
    rideDto.shiftTime ?? dayjs.utc(minShiftTimeMs).toISOString();

  if (direction === DirectionEnum.PickUp) {
    const lastStationTime = lastRealStation?.arrivalTime;

    if (!lastStationTime) {
      Log.warnAndToast(t("errors.notValidArrivalTimeForLastStation"));
      return false;
    }

    const lastStationTimeUpdated = addMinutes(
      lastStationTime,
      timeOffsetMinutes,
    );

    if (
      dateToComparableNumber(lastStationTimeUpdated) >
      dateToComparableNumber(minShiftTime)
    ) {
      const warningMessage = t(
        "errors.directionADateChangedRideArriveAfterShiftStart",
        {
          arrivalTime: formatTimeISO({
            dateTimeISO: lastStationTimeUpdated,
            timeFormat,
            timezone,
          }),
          shiftStartTime: formatTimeISO({
            dateTimeISO: minShiftTime,
            timeFormat,
            timezone,
          }),
        },
      );

      if (allowShiftTimeOverrideDuringManualAction) {
        // if override is enabled, just console log the check as skipped without warning the user
        logShiftTimeCheckSkippedByAllowOverride({
          checkKey: "directionADateChangedRideArriveAfterShiftStart",
          message: warningMessage,
        });
      } else {
        Log.warnAndToast(warningMessage);
      }
    }

    return true;
  }

  const firstStationTime = firstRealStation?.arrivalTime;

  if (!firstStationTime) {
    Log.warnAndToast(t("errors.notValidArrivalTimeForFirstStation"));
    return false;
  }

  const firstStationTimeUpdated = addMinutes(
    firstStationTime,
    timeOffsetMinutes,
  );

  if (
    dateToComparableNumber(firstStationTimeUpdated) <
    dateToComparableNumber(minShiftTime)
  ) {
    const warningMessage = t(
      "errors.directionBDateChangedRideDepartBeforeShiftEnd",
      {
        departureTime: formatTimeISO({
          dateTimeISO: firstStationTimeUpdated,
          timeFormat,
          timezone,
        }),
        shiftEndTime: formatTimeISO({
          dateTimeISO: minShiftTime,
          timeFormat,
          timezone,
        }),
      },
    );

    if (allowShiftTimeOverrideDuringManualAction) {
      // if override is enabled, just console log the check as skipped without warning the user
      logShiftTimeCheckSkippedByAllowOverride({
        checkKey: "directionBDateChangedRideDepartBeforeShiftEnd",
        message: warningMessage,
      });
    } else {
      Log.warnAndToast(warningMessage);
    }
  }

  return true;
};

const updaterPrevStation = ({
  timeShiftMinutes,
  station,
}: {
  timeShiftMinutes: number;
  station: IStationInfo;
}): IStationInfo => {
  if (!station.arrivalTime) {
    return station;
  }

  const arrivalTimeRounded = dateTimeWithoutSeconds(station.arrivalTime);

  const arrivalTimeUpdated = addMinutes(arrivalTimeRounded, timeShiftMinutes);

  return { ...station, arrivalTime: arrivalTimeUpdated };
};

export const updateMetadataStationsTime = ({
  rideDto,
  editStationId,
  timeShiftMinutes,
  isRideHasIndependentDayAdjustment,
}: {
  rideDto: Partial<IRideDto>;
  editStationId: number;
  timeShiftMinutes: number;
  isRideHasIndependentDayAdjustment?: boolean;
}): IRideMetadata => {
  const stations = [...(rideDto?.metadata?.stationsInfo || [])];
  const stationEditIndex = stations.findIndex(
    station => station.id === editStationId,
  );

  const isInnerStation =
    stationEditIndex > 0 && stationEditIndex < stations.length - 1;

  if (isRideHasIndependentDayAdjustment) {
    if (stationEditIndex === INDEX_NOT_FOUND) {
      return rideDto?.metadata || {};
    }

    const updatedStations = stations.map((station, idx) => {
      if (idx !== stationEditIndex) {
        return station;
      }

      if (!station.arrivalTime) {
        return station;
      }

      const arrivalTimeRounded = dateTimeWithoutSeconds(station.arrivalTime);
      const arrivalTimeUpdated = addMinutes(
        arrivalTimeRounded,
        timeShiftMinutes,
      );
      return { ...station, arrivalTime: arrivalTimeUpdated };
    });

    const durationsExtractedFromStations = isInnerStation
      ? extractDurations(updatedStations)
      : rideDto.metadata?.durations;

    const totalDurationComputedForIndependentAdjustment = isInnerStation
      ? calculateTotalDurations({
          durationsFromStations: durationsExtractedFromStations,
          durationsFromMetadata: rideDto.metadata?.durations,
          totalDurationFallback: rideDto.metadata?.totalDuration,
        })
      : rideDto.metadata?.totalDuration;

    return {
      ...(rideDto.metadata || {}),
      stationsInfo: updatedStations,
      durations: durationsExtractedFromStations,
      totalDuration: totalDurationComputedForIndependentAdjustment,
    };
  }

  let newStationsInfo: IStationInfo[];

  if (rideDto.direction === DirectionEnum.PickUp) {
    const updatedPrevStations = stations
      .slice(0, stationEditIndex + 1)
      .map(station =>
        updaterPrevStation({
          station,
          timeShiftMinutes,
        }),
      );

    newStationsInfo = stations.toSpliced(
      0,
      stationEditIndex + 1,
      ...updatedPrevStations,
    );
  } else {
    const updatedPrevStations = stations
      .slice(stationEditIndex, stations.length)
      .map(station =>
        updaterPrevStation({
          station,
          timeShiftMinutes,
        }),
      );
    newStationsInfo = stations.toSpliced(
      stationEditIndex,
      stations.length - stationEditIndex,
      ...updatedPrevStations,
    );
  }

  const durationsFromStations = isInnerStation
    ? extractDurations(newStationsInfo)
    : rideDto.metadata?.durations;

  const totalDurationComputed = calculateTotalDurations({
    durationsFromStations: durationsFromStations,
    durationsFromMetadata: rideDto.metadata?.durations,
    totalDurationFallback: rideDto.metadata?.totalDuration,
  });

  const metadataToUpdate: IRideMetadata = {
    ...(rideDto.metadata || {}),
    stationsInfo: newStationsInfo,
    durations: durationsFromStations,
    totalDuration: totalDurationComputed,
  };
  Log.debug(
    `Ride metadata after time update from index ${stationEditIndex} by ${timeShiftMinutes} min.:`,
    metadataToUpdate,
  );
  return metadataToUpdate;
};

/**
 * Seconds each leg (leg i connects station i -> i + 1) currently spans on the live arrival timeline,
 * `undefined` where either end has no usable time. The timeline is what the editor shows and what the
 * user hand-edits, so it — not `metadata.durations` — is the current plan.
 */
const getRideCurrentLegSeconds = (
  stations: IStationInfo[],
): (number | undefined)[] =>
  stations.slice(1).map((station, index) => {
    const previousTime = stations[index]?.arrivalTime;
    const nextTime = station.arrivalTime;

    if (
      !previousTime ||
      !nextTime ||
      !isValidDateTime(previousTime) ||
      !isValidDateTime(nextTime)
    ) {
      return undefined;
    }

    return Math.max(calculateAbsDurationInSeconds(nextTime, previousTime), 0);
  });

/**
 * Planned seconds per leg for the measured-times preview, `undefined` where the plan is unknown.
 * A ride's arrival timeline already carries second resolution, so it is read directly instead of
 * trusting `metadata.durations`, which the single-station writers only refresh for INNER stations and
 * which therefore goes stale as soon as the first or last station is hand-edited.
 */
export const getRidePlannedLegSeconds = (
  metadata: IRideMetadata | undefined,
): (number | undefined)[] => {
  const stations = metadata?.stationsInfo || [];

  if (stations.length < MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION) {
    return [];
  }

  return getRideCurrentLegSeconds(stations);
};

/**
 * Absolute arrival timeline for a bulk apply, one entry per station covered by `legSeconds`.
 * The anchored end is the one the single-station edit protects: a DropOff ride keeps its departure
 * from the site and grows forward, a PickUp ride keeps its arrival AT the site and every earlier
 * station moves back by the legs still ahead of it. Every value is the anchor plus a running total
 * rather than the previous value plus one leg, so per-leg rounding cannot accumulate.
 * Legs the user did not pick keep the delta they currently have, so a bulk apply cannot revert a
 * station time the user edited by hand and has not saved yet.
 */
const buildRideBulkArrivalTimes = ({
  stations,
  legSeconds,
  direction,
}: {
  stations: IStationInfo[];
  legSeconds: (number | undefined)[];
  direction: DirectionEnum | undefined;
}): TDateISO[] => {
  const legs = resolveAppliedLegSeconds({
    appliedLegSeconds: legSeconds,
    currentLegSeconds: getRideCurrentLegSeconds(stations),
  });
  const isPickUpDirection = direction === DirectionEnum.PickUp;
  const anchorIndex = isPickUpDirection ? legs.length : 0;
  const anchorTime = stations.at(anchorIndex)?.arrivalTime;

  if (!anchorTime || !isValidDateTime(anchorTime)) {
    return [];
  }

  const secondsFromFirstStation = toCumulativeSeconds(legs);
  const anchorOffsetSeconds = secondsFromFirstStation[anchorIndex];

  return secondsFromFirstStation.map(
    seconds =>
      addSeconds(anchorTime, seconds - anchorOffsetSeconds) || anchorTime,
  );
};

/**
 * Signed minutes by which a bulk apply moves the only end of the ride that is free to move: the far
 * end for a DropOff ride, the departure for a PickUp one. This is the offset
 * {@link isUpdatedStationTimeInsideShiftLimits} expects, so a bulk apply can run the same shift-limit
 * validation as a single station time edit.
 */
export const getRideStationsTimeBulkOffsetMinutes = ({
  rideDto,
  legSeconds,
}: {
  rideDto: Partial<IRideDto>;
  legSeconds: (number | undefined)[];
}): number => {
  const stations = rideDto?.metadata?.stationsInfo || [];
  const arrivalTimes = buildRideBulkArrivalTimes({
    stations,
    legSeconds,
    direction: rideDto.direction,
  });

  if (!arrivalTimes.length) {
    return 0;
  }

  const movedIndex =
    rideDto.direction === DirectionEnum.PickUp ? 0 : arrivalTimes.length - 1;
  const previousTime = stations.at(movedIndex)?.arrivalTime;
  const updatedTime = arrivalTimes.at(movedIndex);

  if (!previousTime || !updatedTime) {
    return 0;
  }

  return timeDiffMinutesSigned(previousTime, updatedTime);
};

/**
 * Bulk sibling of {@link updateMetadataStationsTime}: rewrites the arrival time of EVERY station from a
 * per-leg seconds array instead of shifting the neighbours of a single edited one. It keeps the same
 * fixed point as the single edit — the site arrival on a PickUp ride, the site departure on a DropOff
 * one — so applying slower measured times can never push passengers past their shift start.
 * Legs the user did not pick keep the delta they currently have rather than being rebuilt from a stale
 * plan, and stations beyond the end of `legSeconds` keep the time they already had.
 */
export const updateRideMetadataStationsTimeBulk = ({
  rideDto,
  legSeconds,
}: {
  rideDto: Partial<IRideDto>;
  /** One entry per leg; `undefined` means "the user picked nothing here, keep the current delta". */
  legSeconds: (number | undefined)[];
}): IRideMetadata => {
  const stations = rideDto?.metadata?.stationsInfo || [];
  const arrivalTimes = buildRideBulkArrivalTimes({
    stations,
    legSeconds,
    direction: rideDto.direction,
  });

  if (!arrivalTimes.length) {
    Log.warn("Ride bulk station time update skipped: no anchor arrival time");
    return rideDto?.metadata || {};
  }

  const stationsUpdated: IStationInfo[] = stations.map((station, index) => ({
    ...station,
    arrivalTime: arrivalTimes[index] || station.arrivalTime,
  }));

  const durationsFromStations = extractDurations(stationsUpdated);

  const metadataToUpdate: IRideMetadata = {
    ...(rideDto.metadata || {}),
    stationsInfo: stationsUpdated,
    durations: durationsFromStations,
    totalDuration: calculateTotalDurations({
      durationsFromStations,
      durationsFromMetadata: rideDto.metadata?.durations,
      totalDurationFallback: rideDto.metadata?.totalDuration,
    }),
  };
  Log.debug("Ride metadata after bulk station time update:", metadataToUpdate);
  return metadataToUpdate;
};

interface IGetIsUpdatedStationTimeValidArguments {
  rideDto: Partial<IRideDto>;
  station: IStationInfo;
  updatedTime: TDateISO;
  timeFormat: string;
  timezone: TimeZoneEnum;
}

export const getIsUpdatedStationTimeValid = ({
  rideDto,
  station,
  updatedTime,
  timeFormat,
  timezone,
}: IGetIsUpdatedStationTimeValidArguments): SimpleResult<unknown> => {
  if (!rideDto.metadata?.stationsInfo?.length) {
    return new SimpleResult({
      success: false,
      error: t("errors.rideWithNoStations", { rideId: rideDto.id }),
    });
  }

  const stationIndex = rideDto.metadata.stationsInfo.findIndex(stationInfo =>
    areStationsEqual({ a: stationInfo, b: station }),
  );

  if (rideDto.direction === DirectionEnum.PickUp) {
    const isLast =
      rideDto.metadata.stationsInfo.findIndex(stationInfo =>
        areStationsEqual({ a: stationInfo, b: station }),
      ) ===
      rideDto.metadata.stationsInfo.length - 1;

    if (isLast) {
      return new SimpleResult({ success: true });
    }

    const nextStation = rideDto.metadata.stationsInfo.at(
      stationIndex + 1,
    ) as IStationInfo;

    if (!nextStation?.arrivalTime) {
      return new SimpleResult({
        success: false,
        error: t("errors.nextStationDoesntHaveArrivalTimeToCompare", {
          stationId: nextStation.id,
        }),
      });
    }

    const isUpdatedTimeMoreThanNextStationTime =
      timeDiffMinutesSigned(
        dateTimeWithoutSeconds(updatedTime),
        dateTimeWithoutSeconds(nextStation.arrivalTime),
      ) < 0;

    if (isUpdatedTimeMoreThanNextStationTime) {
      return new SimpleResult({
        success: false,
        error: t("errors.updatedTimeForStationMoreThanNextStation", {
          updatedTime: formatTimeISO({
            dateTimeISO: updatedTime,
            timeFormat,
            timezone,
          }),
          stationId: station.id,
          nextStationId: nextStation.id,
          nextStationArrivalTime: formatTimeISO({
            dateTimeISO: nextStation.arrivalTime,
            timeFormat,
            timezone,
          }),
        }),
      });
    }

    return simpleResultTrue;
  }

  // We assume there only two options
  // else if (rideDto.direction === DirectionEnum.DropOff)
  const isFirst =
    rideDto.metadata.stationsInfo.findIndex(stationInfo =>
      areStationsEqual({ a: stationInfo, b: station }),
    ) === 0;

  if (isFirst) {
    return simpleResultTrue;
  }

  const prevStation = rideDto.metadata.stationsInfo.at(
    stationIndex - 1,
  ) as IStationInfo;

  if (!prevStation?.arrivalTime) {
    return new SimpleResult({
      success: false,
      error: t("errors.prevStationDoesntHaveArrivalTimeToCompare", {
        stationId: prevStation.id,
      }),
    });
  }

  const isUpdatedTimeLessThanPrevStationTime =
    timeDiffMinutesSigned(
      dateTimeWithoutSeconds(updatedTime),
      dateTimeWithoutSeconds(prevStation.arrivalTime),
    ) > 0;

  if (isUpdatedTimeLessThanPrevStationTime) {
    return new SimpleResult({
      success: false,
      error: t("errors.updatedTimeForStationLessThanPrevStation", {
        updatedTime: formatTimeISO({
          dateTimeISO: updatedTime,
          timeFormat,
          timezone,
        }),
        stationId: station.id,
        prevStationId: prevStation.id,
        prevStationArrivalTime: formatTimeISO({
          dateTimeISO: prevStation.arrivalTime,
          timeFormat,
          timezone,
        }),
      }),
    });
  }

  return new SimpleResult({ success: true });
};

interface IExtractRideStationsDates {
  rideStart: TDateISO | undefined;
  rideEnd: TDateISO | undefined;
}

export const extractRideStationsDates = (
  stationsInfo: IStationInfo[] | undefined,
): IExtractRideStationsDates => {
  if (!stationsInfo?.length) {
    const emptyDates: IExtractRideStationsDates = {
      rideStart: undefined,
      rideEnd: undefined,
    };
    return emptyDates;
  }

  const { first, last } = getRealRouteEndpoints(stationsInfo);
  const rideStart = first?.arrivalTime;
  const rideEnd = last?.arrivalTime;
  const dates: IExtractRideStationsDates = { rideStart, rideEnd };
  return dates;
};

export const calculateTotalDistance = ({
  distances,
  totalDistanceFallback,
}: {
  distances?: number[];
  totalDistanceFallback?: number;
}): number | undefined => {
  if (!distances?.length) {
    return totalDistanceFallback;
  }

  return _.sum(distances.filter(Number) || []) || totalDistanceFallback;
};

export const calculateTotalDurations = ({
  durationsFromStations,
  durationsFromMetadata,
  totalDurationFallback,
}: {
  durationsFromStations?: number[];
  durationsFromMetadata?: number[];
  totalDurationFallback?: number;
}): number | undefined => {
  if (!durationsFromStations?.length) {
    return (
      _.sum(durationsFromMetadata?.filter(Number) || []) ||
      totalDurationFallback
    );
  }

  return (
    _.sum(durationsFromStations.filter(Number) || []) || totalDurationFallback
  );
};
